import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import axios from 'axios';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { API_BASE } from '../utils';
import EnterpriseHeader from '../components/EnterpriseHeader';
import PadiProIcon from '../components/PadiProIcon';
import { PADIPRO, RADIUS, SPACE, padiproShadow, padiproShadowMd } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

const TYPE_META = {
  jadwal: { label: 'Jadwal', icon: 'calendar', color: PADIPRO.warning, bg: PADIPRO.warningSoft },
  semprot: { label: 'Semprot', icon: 'semprot', color: PADIPRO.primary, bg: PADIPRO.primarySoft },
  pupuk: { label: 'Pupuk', icon: 'pupuk', color: PADIPRO.goldDeep, bg: PADIPRO.goldSoft },
  monitoring: { label: 'Kondisi', icon: 'monitoring', color: PADIPRO.sky, bg: PADIPRO.skySoft },
  foto: { label: 'Foto', icon: 'foto', color: PADIPRO.sky, bg: PADIPRO.skySoft },
  biaya: { label: 'Biaya', icon: 'expenses', color: PADIPRO.goldDeep, bg: PADIPRO.goldSoft },
  catatan: { label: 'Catatan', icon: 'catatan', color: PADIPRO.muted, bg: PADIPRO.surfaceSunken },
  default: { label: 'Catatan', icon: 'catatan', color: PADIPRO.muted, bg: PADIPRO.surfaceSunken },
};

const STATUS_META = {
  overdue: { label: 'Telat', color: PADIPRO.danger, bg: PADIPRO.dangerSoft },
  due: { label: 'Perlu cek', color: PADIPRO.warning, bg: PADIPRO.warningSoft },
  pending: { label: 'Menunggu', color: PADIPRO.warning, bg: PADIPRO.warningSoft },
  scheduled: { label: 'Jadwal', color: PADIPRO.warning, bg: PADIPRO.warningSoft },
  draft: { label: 'Draft', color: PADIPRO.muted, bg: PADIPRO.surfaceSunken },
  done: { label: 'Selesai', color: PADIPRO.primary, bg: PADIPRO.primarySoft },
  confirmed: { label: 'Selesai', color: PADIPRO.primary, bg: PADIPRO.primarySoft },
};

const INPUT_TYPES = [
  { key: 'foto', label: 'Foto kondisi' },
  { key: 'catatan', label: 'Catatan' },
  { key: 'semprot', label: 'Semprot' },
  { key: 'pupuk', label: 'Pupuk' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const toYMD = (d = new Date()) => {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return new Date().toISOString().slice(0, 10);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
const todayYMD = () => toYMD(new Date());
const monthKeyFromDate = (dateKey) => String(dateKey || todayYMD()).slice(0, 7);
const parseLocalDate = (dateKey) => {
  const [y, m, d] = String(dateKey || todayYMD()).split('-').map((x) => parseInt(x, 10));
  return new Date(y || 1970, (m || 1) - 1, d || 1);
};
const addMonths = (monthKey, delta) => {
  const [y, m] = String(monthKey).split('-').map((x) => parseInt(x, 10));
  const d = new Date(y || new Date().getFullYear(), (m || 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};
const monthLabel = (monthKey) => {
  const [y, m] = String(monthKey).split('-').map((x) => parseInt(x, 10));
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
};
const dayLong = (dateKey) =>
  parseLocalDate(dateKey).toLocaleDateString('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
const dayShort = (dateKey) =>
  dateKey
    ? parseLocalDate(dateKey).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })
    : '-';
const formatIDR = (n) => 'Rp' + Number(n || 0).toLocaleString('id-ID');
const notify = (title, message) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.alert) {
    window.alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

function buildCalendarCells(monthKey) {
  const [year, month] = monthKey.split('-').map((x) => parseInt(x, 10));
  const first = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const leading = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let d = 1; d <= lastDay; d += 1) cells.push(`${year}-${pad2(month)}-${pad2(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getPhase(hst) {
  const n = Number(hst);
  if (!Number.isFinite(n)) return 'Belum tanam';
  if (n < 35) return 'Vegetatif';
  if (n < 55) return 'Bunting';
  if (n < 75) return 'Malai';
  if (n < 95) return 'Isi bulir';
  return 'Menguning';
}

export default function BukuSawahScreen({ route, navigation }) {
  const token = route?.params?.token;
  const user = route?.params?.user;
  const authHdr = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { bottomInset, contentPadding, contentWidthStyle, isPhoneLike, isWide } = useResponsive();
  const mounted = useRef(true);

  const [month, setMonth] = useState(() => monthKeyFromDate(todayYMD()));
  const [selectedDate, setSelectedDate] = useState(() => todayYMD());
  const [fieldFilter, setFieldFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');

  const [photoViewer, setPhotoViewer] = useState({ open: false, uri: '', title: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [draftType, setDraftType] = useState('foto');
  const [draftDate, setDraftDate] = useState(() => todayYMD());
  const [draftFieldId, setDraftFieldId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftQty, setDraftQty] = useState('');
  const [draftUnit, setDraftUnit] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [draftPhoto, setDraftPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => () => { mounted.current = false; }, []);

  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const days = Array.isArray(data?.days) ? data.days : [];
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const selectedEntries = useMemo(
    () => entries.filter((item) => item.date_key === selectedDate),
    [entries, selectedDate]
  );
  const dayMap = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
  const calendarCells = useMemo(() => buildCalendarCells(month), [month]);

  const loadJournal = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) return;
      try {
        if (!silent) setLoading(true);
        setErrorText('');
        const params = { month, date: selectedDate, season: 'active' };
        if (fieldFilter !== 'all') params.field_id = fieldFilter;
        const r = await axios.get(`${API_BASE}/operations/journal`, { params, headers: authHdr });
        if (!mounted.current) return;
        setData(r.data || null);
        const list = Array.isArray(r.data?.fields) ? r.data.fields : [];
        if (!draftFieldId && list.length) setDraftFieldId(String(list[0].field_id));
      } catch (e) {
        if (!mounted.current) return;
        setErrorText(e?.response?.data?.error || e?.message || 'Gagal memuat Buku Sawah');
      } finally {
        if (mounted.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [authHdr, draftFieldId, fieldFilter, month, selectedDate, token]
  );

  useFocusEffect(useCallback(() => { loadJournal(); }, [loadJournal]));

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadJournal({ silent: true });
  }, [loadJournal]);

  const pickPhoto = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) return notify('Izin foto', 'Aktifkan izin galeri untuk upload foto sawah.');
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0];
      if (asset?.uri) setDraftPhoto(asset);
    } catch (e) {
      notify('Error', e?.message || 'Gagal memilih foto');
    }
  };

  const openAddModal = () => {
    setDraftDate(selectedDate || todayYMD());
    setDraftFieldId(fieldFilter !== 'all' ? String(fieldFilter) : (fields[0]?.field_id ? String(fields[0].field_id) : null));
    setDraftType('foto');
    setDraftTitle('');
    setDraftQty('');
    setDraftUnit('');
    setDraftNote('');
    setDraftPhoto(null);
    setShowAdd(true);
  };

  const uploadPhoto = async (fieldId, date, note) => {
    if (!draftPhoto?.uri) return null;
    const form = new FormData();
    form.append('date', date);
    form.append('note', note || '');
    if (Platform.OS === 'web') {
      const resp = await fetch(draftPhoto.uri);
      const blob = await resp.blob();
      const fileName = draftPhoto.fileName || `sawah-${Date.now()}.jpg`;
      const fileType = blob.type || draftPhoto.mimeType || 'image/jpeg';
      form.append('photo', new File([blob], fileName, { type: fileType }));
    } else {
      form.append('photo', {
        uri: draftPhoto.uri,
        name: draftPhoto.fileName || `sawah-${Date.now()}.jpg`,
        type: draftPhoto.mimeType || 'image/jpeg',
      });
    }
    return axios.post(`${API_BASE}/operations/fields/${fieldId}/photo`, form, {
      headers: { ...authHdr },
      timeout: 45000,
    });
  };

  const saveNote = async () => {
    const fieldId = Number(draftFieldId);
    if (!Number.isFinite(fieldId) || fieldId <= 0) return notify('Validasi', 'Pilih lahan dulu.');
    const type = String(draftType || 'catatan');
    const note = draftNote.trim();
    const title = draftTitle.trim();
    if (!draftPhoto?.uri && type === 'foto') return notify('Validasi', 'Pilih foto dulu.');
    if (!draftPhoto?.uri && type !== 'foto' && !title && !note) return notify('Validasi', 'Isi catatan atau nama bahan dulu.');

    try {
      setSaving(true);
      const date = toYMD(draftDate);
      if (type !== 'foto') {
        await axios.post(
          `${API_BASE}/operations/events`,
          {
            field_id: fieldId,
            activity_type: type,
            activity_date: date,
            product_or_note: title || note,
            qty: draftQty.trim() || null,
            qty_unit: draftUnit.trim() || null,
          },
          { headers: authHdr }
        );
      }
      if (draftPhoto?.uri) {
        const photoNote = [TYPE_META[type]?.label, title, note].filter(Boolean).join(' - ');
        await uploadPhoto(fieldId, date, photoNote);
      }
      setShowAdd(false);
      await loadJournal({ silent: true });
      notify('Tersimpan', 'Catatan Buku Sawah sudah masuk.');
    } catch (e) {
      notify('Gagal simpan', e?.response?.data?.error || e?.message || 'Catatan belum tersimpan');
    } finally {
      setSaving(false);
    }
  };

  const selectMonth = (nextMonth) => {
    setMonth(nextMonth);
    if (!String(selectedDate || '').startsWith(nextMonth)) setSelectedDate(`${nextMonth}-01`);
  };

  const summary = data?.summary || {};
  const selectedFieldName = fieldFilter === 'all'
    ? 'Semua lahan'
    : fields.find((field) => String(field.field_id) === String(fieldFilter))?.name || 'Lahan';

  const visibleFields = fields.slice(0, isWide ? 8 : 5);
  const recentProblemFields = fields.filter((field) => {
    const lastPhoto = field.last_photo_at ? parseLocalDate(toYMD(field.last_photo_at)) : null;
    const daysSincePhoto = lastPhoto ? Math.floor((parseLocalDate(todayYMD()) - lastPhoto) / 86400000) : 99;
    return daysSincePhoto > 7 || !field.effective_last_spray_at;
  });

  if (!token) {
    return (
      <View style={s.center}>
        <Text style={s.centerTitle}>Buku Sawah</Text>
        <Text style={s.centerText}>Token belum ada. Buka dari dashboard setelah login.</Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={s.screen}
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={[
          s.wrap,
          { padding: contentPadding, paddingBottom: 96 + bottomInset },
          contentWidthStyle,
        ]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
        showsVerticalScrollIndicator={false}
      >
        <View style={s.hero}>
          <EnterpriseHeader
            transparent
            title="Buku Sawah"
            subtitle="Kalender kerja lahan, bukti foto, dan riwayat perawatan padi"
            right={(
              <TouchableOpacity style={s.heroIcon} onPress={onRefresh} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel="Refresh Buku Sawah">
                <PadiProIcon name="calendar" size={18} color="#FFFFFF" strokeWidth={2} />
              </TouchableOpacity>
            )}
          />
          <View style={s.heroLedger}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.heroKicker}>Hari ini</Text>
              <Text style={s.heroDate} numberOfLines={1}>{dayLong(todayYMD())}</Text>
            </View>
            <View style={s.heroBadge}>
              <Text style={s.heroBadgeText}>{data?.season?.name || 'Musim aktif'}</Text>
            </View>
          </View>
          <View style={s.heroStats}>
            <HeroStat label="Lahan" value={summary.active_fields || 0} />
            <HeroStat label="Perlu cek" value={summary.need_check || 0} urgent={(summary.need_check || 0) > 0} />
            <HeroStat label="Biaya tercatat" value={formatIDR(summary.total_amount || 0)} wide />
          </View>
        </View>

        {!!errorText && (
          <View style={s.errorBox}>
            <Text style={s.errorTitle}>Data belum terbaca</Text>
            <Text style={s.errorText}>{errorText}</Text>
          </View>
        )}

        {loading ? (
          <View style={s.loadingBox}>
            <ActivityIndicator color={PADIPRO.primary} />
            <Text style={s.loadingText}>Memuat catatan sawah...</Text>
          </View>
        ) : (
          <>
            <View style={s.summaryGrid}>
              <Metric label="Lahan aktif" value={summary.active_fields || 0} tone="green" icon="inventory" />
              <Metric label="Perlu cek" value={summary.need_check || 0} tone={(summary.need_check || 0) > 0 ? 'warn' : 'green'} icon="monitoring" />
              <Metric label="Telat" value={summary.overdue || 0} tone={(summary.overdue || 0) > 0 ? 'danger' : 'green'} icon="calendar" />
              <Metric label="Ada foto" value={summary.photo_fields || 0} tone="blue" icon="foto" />
            </View>

            <View style={[s.mainGrid, isWide && s.mainGridWide]}>
              <View style={[s.card, s.calendarCard, isWide && s.calendarCardWide]}>
                <View style={s.cardHead}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={s.sectionLabelRow}>
                      <View style={s.sectionMark} />
                      <Text style={s.sectionLabel}>Kalender Operasi</Text>
                    </View>
                    <Text style={s.cardTitle}>Catatan per tanggal</Text>
                    <Text style={s.cardSub}>{selectedFieldName} - {data?.season?.name || 'Musim aktif'}</Text>
                  </View>
                  <View style={s.monthNav}>
                    <TouchableOpacity style={s.monthBtn} onPress={() => selectMonth(addMonths(month, -1))} accessibilityRole="button" accessibilityLabel="Bulan sebelumnya">
                      <Text style={s.monthBtnText}>‹</Text>
                    </TouchableOpacity>
                    <Text style={s.monthText} numberOfLines={1}>{monthLabel(month)}</Text>
                    <TouchableOpacity style={s.monthBtn} onPress={() => selectMonth(addMonths(month, 1))} accessibilityRole="button" accessibilityLabel="Bulan berikutnya">
                      <Text style={s.monthBtnText}>›</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.filterRow}>
                  <Chip label="Semua lahan" active={fieldFilter === 'all'} onPress={() => setFieldFilter('all')} />
                  {fields.map((field) => (
                    <Chip
                      key={field.field_id}
                      label={field.name}
                      active={String(fieldFilter) === String(field.field_id)}
                      onPress={() => setFieldFilter(String(field.field_id))}
                    />
                  ))}
                </ScrollView>

                <View style={s.calendarSurface}>
                  <View style={s.weekRow}>
                    {['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map((label) => (
                      <Text key={label} style={s.weekText}>{label}</Text>
                    ))}
                  </View>
                  <View style={s.calendarGrid}>
                    {calendarCells.map((dateKey, idx) => (
                      <DayCell
                        key={dateKey || `empty-${idx}`}
                        dateKey={dateKey}
                        info={dateKey ? dayMap.get(dateKey) : null}
                        selected={dateKey === selectedDate}
                        today={dateKey === todayYMD()}
                        onPress={() => dateKey && setSelectedDate(dateKey)}
                      />
                    ))}
                  </View>
                </View>
                <View style={s.legendRow}>
                  <Legend color={PADIPRO.primary} text="selesai" />
                  <Legend color={PADIPRO.warning} text="jadwal" />
                  <Legend color={PADIPRO.danger} text="telat" />
                  <Legend color={PADIPRO.sky} text="foto" />
                </View>
              </View>

              <View style={[s.card, s.notesCard, isWide && s.notesCardWide]}>
                <View style={s.cardHead}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={s.sectionLabelRow}>
                      <View style={[s.sectionMark, { backgroundColor: PADIPRO.sky }]} />
                      <Text style={s.sectionLabel}>Bukti Harian</Text>
                    </View>
                    <Text style={s.cardTitle}>Catatan {dayShort(selectedDate)}</Text>
                    <Text style={s.cardSub}>{selectedEntries.length} catatan pada tanggal ini</Text>
                  </View>
                  <TouchableOpacity
                    style={s.smallAction}
                    onPress={openAddModal}
                    activeOpacity={0.9}
                    accessibilityRole="button"
                    accessibilityLabel="Tambah catatan pada tanggal terpilih"
                  >
                    <PadiProIcon name="catatan" size={14} color="#FFFFFF" strokeWidth={2} />
                    <Text style={s.smallActionText}>Catatan</Text>
                  </TouchableOpacity>
                </View>

                {selectedEntries.length ? (
                  <View style={s.entryList}>
                    {selectedEntries.map((entry) => (
                      <EntryRow
                        key={`${entry.kind}-${entry.source_id}-${entry.field_id}-${entry.date_key}`}
                        entry={entry}
                        onOpenPhoto={(uri, title) => setPhotoViewer({ open: true, uri, title })}
                      />
                    ))}
                  </View>
                ) : (
                  <View style={s.emptyDate}>
                    <View style={s.emptyIcon}>
                      <PadiProIcon name="calendar" size={20} color={PADIPRO.primary} strokeWidth={2} />
                    </View>
                    <Text style={s.emptyTitle}>Belum ada catatan</Text>
                    <Text style={s.emptyText}>Pilih tanggal lain atau tambah foto/catatan untuk lahan ini.</Text>
                  </View>
                )}
              </View>
            </View>

            <View style={s.card}>
              <View style={s.cardHead}>
                <View>
                  <View style={s.sectionLabelRow}>
                    <View style={[s.sectionMark, { backgroundColor: PADIPRO.gold }]} />
                    <Text style={s.sectionLabel}>Audit Lahan</Text>
                  </View>
                  <Text style={s.cardTitle}>Pantauan Lahan</Text>
                  <Text style={s.cardSub}>Ringkas untuk lihat perkembangan padi</Text>
                </View>
              </View>
              <View style={s.fieldList}>
                {visibleFields.map((field) => (
                  <FieldRow key={field.field_id} field={field} onPress={() => setFieldFilter(String(field.field_id))} />
                ))}
              </View>
              {fields.length > visibleFields.length && (
                <Text style={s.moreText}>+{fields.length - visibleFields.length} lahan lain. Pakai filter untuk lihat detail.</Text>
              )}
              {!!recentProblemFields.length && (
                <View style={s.auditHint}>
                  <Text style={s.auditHintTitle}>{recentProblemFields.length} lahan perlu dicek</Text>
                  <Text style={s.auditHintText}>Biasanya karena belum ada foto baru atau semprot terakhir belum tercatat.</Text>
                </View>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <TouchableOpacity
        style={[s.fab, { bottom: 18 + bottomInset }]}
        onPress={openAddModal}
        activeOpacity={0.92}
        accessibilityRole="button"
        accessibilityLabel="Tambah foto atau catatan Buku Sawah"
      >
        <PadiProIcon name="foto" size={18} color="#FFFFFF" strokeWidth={2} />
        <Text style={s.fabText}>+ Foto / Catatan</Text>
      </TouchableOpacity>

      <Modal visible={photoViewer.open} transparent animationType="fade" onRequestClose={() => setPhotoViewer({ open: false, uri: '', title: '' })}>
        <View style={s.viewerBackdrop}>
          <View style={s.viewerCard}>
            <Text style={s.viewerTitle}>{photoViewer.title || 'Foto sawah'}</Text>
            {!!photoViewer.uri && <Image source={{ uri: photoViewer.uri }} style={s.viewerImage} resizeMode="contain" />}
            <TouchableOpacity style={s.viewerClose} onPress={() => setPhotoViewer({ open: false, uri: '', title: '' })}>
              <Text style={s.viewerCloseText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={showAdd} transparent animationType="slide" onRequestClose={() => setShowAdd(false)}>
        <View style={s.modalBackdrop}>
          <View style={[s.modalCard, isPhoneLike && s.modalCardPhone]}>
            <Text style={s.modalTitle}>Tambah Catatan</Text>
            <Text style={s.modalSub}>Masukkan bukti lapangan tanpa form yang panjang.</Text>

            <Text style={s.label}>Tanggal</Text>
            <TextInput style={s.input} value={draftDate} onChangeText={setDraftDate} placeholder="YYYY-MM-DD" placeholderTextColor={PADIPRO.mutedSoft} />

            <Text style={s.label}>Lahan</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.filterRow}>
              {fields.map((field) => (
                <Chip
                  key={`pick-${field.field_id}`}
                  label={field.name}
                  active={String(draftFieldId) === String(field.field_id)}
                  onPress={() => setDraftFieldId(String(field.field_id))}
                />
              ))}
            </ScrollView>

            <Text style={s.label}>Jenis</Text>
            <View style={s.typeGrid}>
              {INPUT_TYPES.map((item) => (
                <TouchableOpacity
                  key={item.key}
                  style={[s.typeBtn, draftType === item.key && s.typeBtnActive]}
                  onPress={() => setDraftType(item.key)}
                  activeOpacity={0.9}
                  accessibilityRole="button"
                  accessibilityState={{ selected: draftType === item.key }}
                  accessibilityLabel={`Pilih jenis ${item.label}`}
                >
                  <PadiProIcon
                    name={TYPE_META[item.key]?.icon || 'catatan'}
                    size={15}
                    color={draftType === item.key ? PADIPRO.primaryDark : PADIPRO.muted}
                    strokeWidth={2}
                  />
                  <Text style={[s.typeText, draftType === item.key && s.typeTextActive]}>{item.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {draftType !== 'foto' && (
              <>
                <Text style={s.label}>Nama bahan / isi singkat</Text>
                <TextInput
                  style={s.input}
                  value={draftTitle}
                  onChangeText={setDraftTitle}
                  placeholder="contoh: Xenon, KCL, cek gulma"
                  placeholderTextColor={PADIPRO.mutedSoft}
                />
                <View style={s.row}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.label}>Qty</Text>
                    <TextInput style={s.input} value={draftQty} onChangeText={setDraftQty} keyboardType="decimal-pad" placeholder="1" placeholderTextColor={PADIPRO.mutedSoft} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={s.label}>Satuan</Text>
                    <TextInput style={s.input} value={draftUnit} onChangeText={setDraftUnit} placeholder="pcs / kg" placeholderTextColor={PADIPRO.mutedSoft} />
                  </View>
                </View>
              </>
            )}

            <Text style={s.label}>Catatan</Text>
            <TextInput
              style={[s.input, s.textArea]}
              value={draftNote}
              onChangeText={setDraftNote}
              multiline
              placeholder="contoh: daun hijau, air normal, setelah semprot"
              placeholderTextColor={PADIPRO.mutedSoft}
            />

            <TouchableOpacity style={s.photoPick} onPress={pickPhoto} activeOpacity={0.9}>
              <PadiProIcon name="foto" size={17} color={PADIPRO.primary} strokeWidth={2} />
              <Text style={s.photoPickText}>{draftPhoto ? 'Ganti foto' : 'Upload foto opsional'}</Text>
            </TouchableOpacity>
            {!!draftPhoto?.uri && <Image source={{ uri: draftPhoto.uri }} style={s.photoPreview} resizeMode="cover" />}

            <View style={s.modalActions}>
              <TouchableOpacity style={[s.modalBtn, s.modalBtnGhost]} onPress={() => setShowAdd(false)} disabled={saving}>
                <Text style={s.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.modalBtn, s.modalBtnPrimary, saving && { opacity: 0.7 }]} onPress={saveNote} disabled={saving}>
                <Text style={s.modalBtnPrimaryText}>{saving ? 'Menyimpan...' : 'Simpan'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

function HeroStat({ label, value, urgent = false, wide = false }) {
  return (
    <View style={[s.heroStat, wide && s.heroStatWide, urgent && s.heroStatUrgent]}>
      <Text style={s.heroStatValue} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
      <Text style={s.heroStatLabel} numberOfLines={1}>{label}</Text>
    </View>
  );
}

function Metric({ label, value, tone, icon }) {
  const colors = {
    green: { bg: PADIPRO.primarySoft, color: PADIPRO.primary },
    warn: { bg: PADIPRO.warningSoft, color: PADIPRO.warning },
    danger: { bg: PADIPRO.dangerSoft, color: PADIPRO.danger },
    blue: { bg: PADIPRO.skySoft, color: PADIPRO.sky },
  }[tone] || { bg: PADIPRO.surfaceSunken, color: PADIPRO.text };
  return (
    <View style={[s.metric, { backgroundColor: colors.bg, borderColor: `${colors.color}35` }]}>
      <View style={[s.metricIcon, { backgroundColor: '#FFFFFF', borderColor: `${colors.color}2A` }]}>
        <PadiProIcon name={icon || 'report'} size={16} color={colors.color} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[s.metricValue, { color: colors.color }]} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
        <Text style={s.metricLabel} numberOfLines={1}>{label}</Text>
      </View>
    </View>
  );
}

function Chip({ label, active, onPress }) {
  return (
    <TouchableOpacity
      style={[s.chip, active && s.chipActive]}
      onPress={onPress}
      activeOpacity={0.9}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={`Filter ${label}`}
    >
      <Text style={[s.chipText, active && s.chipTextActive]} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
  );
}

function Legend({ color, text }) {
  return (
    <View style={s.legendItem}>
      <View style={[s.legendDot, { backgroundColor: color }]} />
      <Text style={s.legendText}>{text}</Text>
    </View>
  );
}

function DayCell({ dateKey, info, selected, today, onPress }) {
  if (!dateKey) return <View style={s.dayCellBlank} />;
  const d = parseInt(dateKey.slice(-2), 10);
  const hasOverdue = Number(info?.overdue || 0) > 0;
  const hasDue = Number(info?.due || 0) > 0 || Number(info?.scheduled || 0) > 0;
  const hasDone = Number(info?.done || 0) > 0;
  const hasPhoto = Number(info?.photos || 0) > 0;
  const total = Number(info?.total || 0);
  return (
    <TouchableOpacity
      style={[s.dayCell, selected && s.dayCellSelected, today && !selected && s.dayCellToday]}
      onPress={onPress}
      activeOpacity={0.9}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={`${dayLong(dateKey)}, ${total} catatan`}
    >
      <Text style={[s.dayNum, selected && s.dayNumSelected]}>{d}</Text>
      {total > 0 && (
        <Text style={[s.dayCount, selected && s.dayCountSelected]} numberOfLines={1}>
          {total}
        </Text>
      )}
      <View style={s.dayDots}>
        {hasOverdue && <View style={[s.dayDot, { backgroundColor: PADIPRO.danger }]} />}
        {hasDue && <View style={[s.dayDot, { backgroundColor: PADIPRO.warning }]} />}
        {hasDone && <View style={[s.dayDot, { backgroundColor: PADIPRO.primary }]} />}
        {hasPhoto && <View style={[s.dayDot, { backgroundColor: PADIPRO.sky }]} />}
      </View>
    </TouchableOpacity>
  );
}

function EntryRow({ entry, onOpenPhoto }) {
  const kind = String(entry.kind || 'default').toLowerCase();
  const meta = TYPE_META[kind] || TYPE_META.default;
  const status = STATUS_META[String(entry.status || '').toLowerCase()] || STATUS_META.done;
  const qtyText = entry.qty != null ? `${Number(entry.qty).toLocaleString('id-ID')}${entry.qty_unit ? ` ${entry.qty_unit}` : ''}` : '';
  const amountText = Number(entry.amount || 0) > 0 ? formatIDR(entry.amount) : '';
  const subParts = [entry.subtitle, qtyText, amountText, entry.operator_name ? `oleh ${entry.operator_name}` : null].filter(Boolean);

  return (
    <View style={s.entryRow}>
      <View style={[s.entryAccent, { backgroundColor: meta.color }]} />
      <View style={[s.entryIcon, { backgroundColor: meta.bg, borderColor: `${meta.color}35` }]}>
        <PadiProIcon name={meta.icon} size={17} color={meta.color} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={s.entryTop}>
          <Text style={s.entryField} numberOfLines={1}>{entry.field_name || 'Lahan'}</Text>
          <View style={[s.statusPill, { backgroundColor: status.bg, borderColor: `${status.color}35` }]}>
            <Text style={[s.statusText, { color: status.color }]}>{status.label}</Text>
          </View>
        </View>
        <Text style={s.entryTitle} numberOfLines={2}>{entry.title || meta.label}</Text>
        {!!subParts.length && <Text style={s.entrySub} numberOfLines={2}>{subParts.join(' - ')}</Text>}
        <View style={s.entryMetaRow}>
          <Text style={s.entrySource} numberOfLines={1}>Sumber: {entry.source || entry.ref_table || 'catatan'}</Text>
          <Text style={s.entrySource} numberOfLines={1}>#{entry.ref_id || entry.source_id || '-'}</Text>
        </View>
        {!!entry.photo_url && (
          <TouchableOpacity style={s.thumbWrap} onPress={() => onOpenPhoto(entry.photo_url, entry.title || entry.field_name)} activeOpacity={0.9}>
            <Image source={{ uri: entry.photo_url }} style={s.thumb} resizeMode="cover" />
            <Text style={s.thumbText}>Lihat foto</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

function FieldRow({ field, onPress }) {
  const hst = Number(field.hst);
  const hasHst = Number.isFinite(hst);
  const phase = getPhase(hst);
  const condition = [field.last_water_level ? `air ${field.last_water_level}` : null, field.last_leaf_condition ? `daun ${field.last_leaf_condition}` : null]
    .filter(Boolean)
    .join(', ');
  const harvestDays = Number(field.harvest_days || 100);
  const progress = hasHst ? Math.min(100, Math.max(0, (hst / (Number.isFinite(harvestDays) && harvestDays > 0 ? harvestDays : 100)) * 100)) : 0;
  return (
    <TouchableOpacity
      style={s.fieldRow}
      onPress={onPress}
      activeOpacity={0.9}
      accessibilityRole="button"
      accessibilityLabel={`Filter lahan ${field.name}`}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={s.fieldTop}>
          <Text style={s.fieldName} numberOfLines={1}>{field.name}</Text>
          <View style={s.phasePill}>
            <Text style={s.phasePillText} numberOfLines={1}>{hasHst ? `HST ${hst}` : phase}</Text>
          </View>
        </View>
        <Text style={s.fieldMeta} numberOfLines={1}>{phase}</Text>
        <View style={s.progressTrack}>
          <View style={[s.progressFill, { width: `${progress}%` }]} />
        </View>
        <Text style={s.fieldSub} numberOfLines={1}>
          Semprot {field.effective_last_spray_at ? dayShort(toYMD(field.effective_last_spray_at)) : '-'} - Foto {field.last_photo_at ? dayShort(toYMD(field.last_photo_at)) : '-'}
        </Text>
        {!!condition && <Text style={s.fieldCondition} numberOfLines={1}>{condition}</Text>}
      </View>
      <Text style={s.fieldCost} numberOfLines={1}>{formatIDR(field.season_cost)}</Text>
    </TouchableOpacity>
  );
}

const s = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#F3F0E8' },
  wrap: { gap: SPACE.lg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: PADIPRO.bg },
  centerTitle: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 18 },
  centerText: { marginTop: 8, color: PADIPRO.muted, fontWeight: '700', textAlign: 'center' },
  hero: {
    position: 'relative',
    borderRadius: RADIUS.xl,
    backgroundColor: PADIPRO.primary,
    borderWidth: 1,
    borderColor: PADIPRO.primaryDark,
    borderLeftWidth: 5,
    borderLeftColor: PADIPRO.gold,
    padding: SPACE.lg,
    overflow: 'hidden',
    ...padiproShadowMd,
  },
  heroLedger: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.18)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  heroKicker: { color: '#AFC9B8', fontWeight: '900', fontSize: 10, textTransform: 'uppercase' },
  heroDate: { marginTop: 2, color: '#F7FFF8', fontWeight: '900', fontSize: 13 },
  heroBadge: {
    minHeight: 34,
    borderRadius: 17,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.24)',
  },
  heroBadgeText: { color: '#FFFFFF', fontWeight: '900', fontSize: 11 },
  heroStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  heroStat: {
    flexGrow: 1,
    minWidth: 92,
    minHeight: 58,
    borderRadius: RADIUS.md,
    paddingHorizontal: 12,
    paddingVertical: 9,
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  heroStatWide: { minWidth: 150 },
  heroStatUrgent: { backgroundColor: 'rgba(166,124,0,0.22)', borderColor: 'rgba(247,236,209,0.34)' },
  heroStatValue: { color: '#FFFFFF', fontWeight: '900', fontSize: 18, fontVariant: ['tabular-nums'] },
  heroStatLabel: { marginTop: 2, color: '#CFE0D2', fontWeight: '800', fontSize: 10.5 },
  heroIcon: {
    width: 44,
    height: 44,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.34)',
  },
  loadingBox: {
    minHeight: 220,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: RADIUS.lg,
    backgroundColor: PADIPRO.surface,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  loadingText: { marginTop: 10, color: PADIPRO.muted, fontWeight: '800' },
  errorBox: { borderRadius: RADIUS.md, borderWidth: 1, borderColor: `${PADIPRO.danger}44`, backgroundColor: PADIPRO.dangerSoft, padding: 12 },
  errorTitle: { color: PADIPRO.danger, fontWeight: '900' },
  errorText: { marginTop: 4, color: PADIPRO.text, fontWeight: '700' },
  summaryGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  metric: {
    flexBasis: '48%',
    flexGrow: 1,
    minHeight: 74,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  metricIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  metricValue: { fontSize: 23, fontWeight: '900', fontVariant: ['tabular-nums'] },
  metricLabel: { marginTop: 1, color: PADIPRO.text, fontWeight: '800', fontSize: 11 },
  mainGrid: { gap: SPACE.lg },
  mainGridWide: { flexDirection: 'row', alignItems: 'flex-start' },
  card: { borderRadius: RADIUS.lg, backgroundColor: PADIPRO.surface, borderWidth: 1, borderColor: '#DDD5BE', padding: SPACE.md, ...padiproShadow },
  calendarCard: {},
  calendarCardWide: { flex: 1.35 },
  notesCard: {},
  notesCardWide: { flex: 1, alignSelf: 'stretch' },
  cardHead: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 },
  sectionLabelRow: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 4 },
  sectionMark: { width: 18, height: 4, borderRadius: 2, backgroundColor: PADIPRO.primary },
  sectionLabel: { color: PADIPRO.muted, fontWeight: '900', fontSize: 10, textTransform: 'uppercase' },
  cardTitle: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 17 },
  cardSub: { color: PADIPRO.muted, fontWeight: '700', fontSize: 12, marginTop: 2 },
  monthNav: { flexDirection: 'row', alignItems: 'center', gap: 7, maxWidth: 210 },
  monthBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border },
  monthBtnText: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 20, lineHeight: 22 },
  monthText: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 12, maxWidth: 118, textAlign: 'center' },
  filterRow: { gap: 8, paddingVertical: 4 },
  chip: { minHeight: 38, paddingHorizontal: 13, borderRadius: 19, borderWidth: 1, borderColor: '#DDD5BE', backgroundColor: '#FFFDF8' },
  chipActive: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.primarySoft },
  chipText: { color: PADIPRO.muted, fontWeight: '800', lineHeight: 36 },
  chipTextActive: { color: PADIPRO.primaryDark },
  calendarSurface: { marginTop: 10, borderRadius: RADIUS.md, backgroundColor: '#FBFAF5', borderWidth: 1, borderColor: '#E9E1CC', padding: 8 },
  weekRow: { flexDirection: 'row' },
  weekText: { flex: 1, textAlign: 'center', color: PADIPRO.muted, fontWeight: '900', fontSize: 10 },
  calendarGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 },
  dayCell: {
    width: `${100 / 7}%`,
    aspectRatio: 1.02,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: RADIUS.sm,
    borderWidth: 1,
    borderColor: 'transparent',
    minHeight: 44,
  },
  dayCellBlank: { width: `${100 / 7}%`, aspectRatio: 1.02 },
  dayCellSelected: { backgroundColor: PADIPRO.primary, borderColor: PADIPRO.primaryDark },
  dayCellToday: { borderColor: PADIPRO.gold, backgroundColor: '#FFF7DF' },
  dayNum: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 13 },
  dayNumSelected: { color: '#FFFFFF' },
  dayCount: { marginTop: 1, color: PADIPRO.muted, fontWeight: '900', fontSize: 9, lineHeight: 11 },
  dayCountSelected: { color: '#DDEBE1' },
  dayDots: { marginTop: 3, height: 6, flexDirection: 'row', justifyContent: 'center', gap: 2 },
  dayDot: { width: 5, height: 5, borderRadius: 3 },
  legendRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 7, height: 7, borderRadius: 4 },
  legendText: { color: PADIPRO.muted, fontWeight: '800', fontSize: 11 },
  smallAction: { minHeight: 40, paddingHorizontal: 12, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: PADIPRO.primary, borderWidth: 1, borderColor: PADIPRO.primaryDark, flexDirection: 'row', gap: 7 },
  smallActionText: { color: '#FFFFFF', fontWeight: '900', fontSize: 12 },
  entryList: { gap: 10 },
  entryRow: { position: 'relative', flexDirection: 'row', gap: 10, borderRadius: RADIUS.md, borderWidth: 1, borderColor: '#E5DEC9', backgroundColor: '#FFFDF8', padding: 10, paddingLeft: 13, overflow: 'hidden' },
  entryAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 4 },
  entryIcon: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  entryTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  entryField: { flex: 1, color: PADIPRO.textStrong, fontWeight: '900', fontSize: 13 },
  statusPill: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 3 },
  statusText: { fontWeight: '900', fontSize: 10 },
  entryTitle: { marginTop: 4, color: PADIPRO.text, fontWeight: '900', fontSize: 13, lineHeight: 18 },
  entrySub: { marginTop: 3, color: PADIPRO.muted, fontWeight: '700', fontSize: 11.5, lineHeight: 16 },
  entryMetaRow: { marginTop: 7, flexDirection: 'row', alignItems: 'center', gap: 8 },
  entrySource: { color: PADIPRO.mutedSoft, fontWeight: '800', fontSize: 10 },
  thumbWrap: { marginTop: 9, borderRadius: RADIUS.md, borderWidth: 1, borderColor: PADIPRO.border, overflow: 'hidden', backgroundColor: PADIPRO.surfaceSunken },
  thumb: { width: '100%', height: 132 },
  thumbText: { padding: 8, textAlign: 'center', color: PADIPRO.primary, fontWeight: '900', fontSize: 11 },
  emptyDate: { minHeight: 156, borderRadius: RADIUS.md, backgroundColor: '#FBFAF5', borderWidth: 1, borderColor: '#E9E1CC', padding: 16, alignItems: 'center', justifyContent: 'center' },
  emptyIcon: { width: 46, height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', backgroundColor: PADIPRO.primarySoft, borderWidth: 1, borderColor: PADIPRO.primarySoftStrong, marginBottom: 9 },
  emptyTitle: { color: PADIPRO.textStrong, fontWeight: '900' },
  emptyText: { marginTop: 4, color: PADIPRO.muted, fontWeight: '700', lineHeight: 18, textAlign: 'center' },
  fieldList: { gap: 8 },
  fieldRow: { flexDirection: 'row', gap: 10, alignItems: 'center', borderRadius: RADIUS.md, borderWidth: 1, borderColor: '#E5DEC9', backgroundColor: '#FFFDF8', padding: 11 },
  fieldTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  fieldName: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 13 },
  phasePill: { maxWidth: 92, minHeight: 24, borderRadius: 12, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: PADIPRO.primarySoft, borderWidth: 1, borderColor: PADIPRO.primarySoftStrong },
  phasePillText: { color: PADIPRO.primaryDark, fontWeight: '900', fontSize: 10 },
  fieldMeta: { marginTop: 3, color: PADIPRO.primary, fontWeight: '900', fontSize: 11.5 },
  progressTrack: { marginTop: 7, height: 6, borderRadius: 3, backgroundColor: '#EEE7D6', overflow: 'hidden' },
  progressFill: { height: 6, borderRadius: 3, backgroundColor: PADIPRO.primary },
  fieldSub: { marginTop: 6, color: PADIPRO.muted, fontWeight: '700', fontSize: 11 },
  fieldCondition: { marginTop: 2, color: PADIPRO.sky, fontWeight: '800', fontSize: 11 },
  fieldCost: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 12, maxWidth: 96, textAlign: 'right' },
  moreText: { marginTop: 8, color: PADIPRO.muted, fontWeight: '800', fontSize: 11 },
  auditHint: { marginTop: 10, borderRadius: RADIUS.md, borderWidth: 1, borderColor: `${PADIPRO.warning}44`, backgroundColor: PADIPRO.warningSoft, padding: 10 },
  auditHintTitle: { color: PADIPRO.warning, fontWeight: '900' },
  auditHintText: { color: PADIPRO.text, fontWeight: '700', fontSize: 11.5, marginTop: 3 },
  fab: { position: 'absolute', left: 18, right: 18, minHeight: 54, borderRadius: 27, backgroundColor: PADIPRO.primary, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: PADIPRO.primaryDark, flexDirection: 'row', gap: 8, ...padiproShadowMd },
  fabText: { color: '#FFFFFF', fontWeight: '900', fontSize: 15 },
  viewerBackdrop: { flex: 1, backgroundColor: 'rgba(12,20,16,0.86)', alignItems: 'center', justifyContent: 'center', padding: 18 },
  viewerCard: { width: '100%', maxWidth: 760 },
  viewerTitle: { color: '#FFFFFF', fontWeight: '900', marginBottom: 10 },
  viewerImage: { width: '100%', height: 520, maxHeight: '78%', backgroundColor: '#111' },
  viewerClose: { marginTop: 12, minHeight: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' },
  viewerCloseText: { color: PADIPRO.primary, fontWeight: '900' },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(18,32,26,0.38)', justifyContent: 'flex-end' },
  modalCard: { maxHeight: '92%', backgroundColor: PADIPRO.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 16, borderWidth: 1, borderColor: PADIPRO.border },
  modalCardPhone: { paddingBottom: 18 },
  modalTitle: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 18 },
  modalSub: { marginTop: 3, color: PADIPRO.muted, fontWeight: '700', lineHeight: 18 },
  label: { marginTop: 11, marginBottom: 5, color: PADIPRO.textStrong, fontWeight: '900', fontSize: 12 },
  input: { minHeight: 44, borderRadius: RADIUS.md, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken, paddingHorizontal: 12, color: PADIPRO.textStrong, fontWeight: '800' },
  textArea: { minHeight: 74, paddingTop: 10, textAlignVertical: 'top' },
  row: { flexDirection: 'row', gap: 10 },
  typeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  typeBtn: { minHeight: 42, borderRadius: 21, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surface, paddingHorizontal: 12, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 7 },
  typeBtnActive: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.primarySoft },
  typeText: { color: PADIPRO.muted, fontWeight: '900', fontSize: 12 },
  typeTextActive: { color: PADIPRO.primaryDark },
  photoPick: { marginTop: 12, minHeight: 44, borderRadius: RADIUS.md, borderWidth: 1, borderColor: PADIPRO.primarySoftStrong, backgroundColor: PADIPRO.primarySoft, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  photoPickText: { color: PADIPRO.primary, fontWeight: '900' },
  photoPreview: { marginTop: 10, width: '100%', height: 150, borderRadius: RADIUS.md, backgroundColor: PADIPRO.surfaceSunken },
  modalActions: { flexDirection: 'row', gap: 10, marginTop: 14 },
  modalBtn: { flex: 1, minHeight: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  modalBtnGhost: { backgroundColor: PADIPRO.surface, borderColor: PADIPRO.border },
  modalBtnPrimary: { backgroundColor: PADIPRO.primary, borderColor: PADIPRO.primaryDark },
  modalBtnGhostText: { color: PADIPRO.textStrong, fontWeight: '900' },
  modalBtnPrimaryText: { color: '#FFFFFF', fontWeight: '900' },
});
